// react
import React from 'react';
import styled from 'styled-components';

// components
import { Title } from '@/components/atoms/Title/Title';

import { Button } from '@/components/atoms/Button/Button';
import { Container } from '@/components/atoms/Container/Container';
import { RowAction, Table } from '@/components/organisms/Table/Table';
import { Content } from '@/components/templates/Content/Content';

// tanstack
import { ColumnDef } from '@tanstack/react-table';

// hooks
import { useWindowSize } from '@/hooks';

// theme
import { theme } from '@/theme';

/**
 * TablePage is an editor for creating a new care profile.
 */
export const TablePage = ({
  title,
  description,
  columns,
  rows,
  actions,
  toolbarActions,
  isTotalRows = true,
  rowsPerPage,
  onBack,
  isTitleHidden = false,
}: TablePageProps) => {
  // hooks
  const [width] = useWindowSize();

  return (
    <Content
      content={
        <Container size='full'>
          {onBack && (
            <Button
              ariaLabel='back'
              iconLeft='chevron-left'
              variant='secondary'
              size='sm'
              onPress={onBack}
            >
              Back
            </Button>
          )}
          <Wrapper>
            {!isTitleHidden && (
              <div>
                <Title>{title}</Title>
                {description && <Description>{description}</Description>}
              </div>
            )}
            <Table
              columns={columns}
              rows={rows}
              actions={actions}
              toolbarActions={toolbarActions}
              isMobile={width < theme.breakpoint.mdValue}
              isTotalRows={isTotalRows}
              rowsPerPage={rowsPerPage}
            />
          </Wrapper>
        </Container>
      }
    />
  );
};

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${(props) => props.theme.spacing[8]};
  width: 100%;
`;

const Description = styled.div`
  font: ${(props) => props.theme.typography.heading['2xl']};
  color: ${(props) => props.theme.color.neutral[90]};
`;

interface TablePageProps {
  title: string;
  description?: string;
  columns: ColumnDef<any, any>[];
  rows?: any[];
  actions?: RowAction[];
  toolbarActions?: React.ReactNode;
  isTotalRows?: boolean;
  rowsPerPage?: number;
  onBack?: () => void;
  isTitleHidden?: boolean;
}

TablePage.displayName = 'TablePage';

export default TablePage;
